import { Button, Grid, Stack, TextField, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import useAuth from "hooks/useAuth";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "store";
import { gridSpacing } from "store/constant";
import { openSnackbar } from "store/slices/snackbar";
import SubCard from "ui-component/cards/SubCard";
import AnimateButton from "ui-component/extended/AnimateButton";
import { passwordValidator } from "utils/validators";
import * as Yup from "yup";

const Security = () => {
  const { changePassword } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const validation = Yup.object().shape({
    oldPassword: Yup.string().required(<FormattedMessage id="required" />),
    newPassword1: passwordValidator().required(
      <FormattedMessage id="required" />
    ),
    newPassword2: Yup.string()
      .required(<FormattedMessage id="required" />)
      .oneOf(
        [Yup.ref("newPassword1")],
        <FormattedMessage id="profil-security-password" />
      ),
  });

  return (
    <Formik
      initialValues={{
        oldPassword: "",
        newPassword1: "",
        newPassword2: "",
      }}
      validationSchema={validation}
      onSubmit={async (values) => {
        try {
          await changePassword(values.oldPassword, values.newPassword1);
          dispatch(
            openSnackbar({
              open: true,
              message: <FormattedMessage id="security-password" />,
              variant: "alert",
              alert: {
                color: "success",
              },
              close: false,
            })
          );
          navigate("/dashboard/login", { replace: true });
        } catch (err) {
          dispatch(
            openSnackbar({
              open: true,
              message: <FormattedMessage id="security-password-error" />,
              variant: "alert",
              alert: {
                color: "error",
              },
              close: false,
            })
          );
        }
      }}
    >
      {(formik) => (
        <Form>
          <Grid container spacing={gridSpacing}>
            <Grid item sm={6} md={8}>
              <Grid container spacing={gridSpacing}>
                <Grid item xs={12}>
                  <SubCard
                    title={
                      <FormattedMessage id="profile.security.change_password" />
                    }
                  >
                    <Grid container spacing={gridSpacing}>
                      <Grid item xs={12}>
                        <TextField
                          type="password"
                          name="oldPassword"
                          label={
                            <FormattedMessage id="profile.security.current_password" />
                          }
                          value={formik.values.oldPassword}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.oldPassword &&
                            Boolean(formik.errors.oldPassword)
                          }
                          helperText={
                            formik.touched.oldPassword &&
                            formik.errors.oldPassword
                          }
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          type="password"
                          name="newPassword1"
                          label={
                            <FormattedMessage id="profile.security.new_password" />
                          }
                          value={formik.values.newPassword1}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.newPassword1 &&
                            Boolean(formik.errors.newPassword1)
                          }
                          helperText={
                            formik.touched.newPassword1 &&
                            formik.errors.newPassword1
                          }
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          type="password"
                          name="newPassword2"
                          label={
                            <FormattedMessage id="profile.security.new_password_confirmation" />
                          }
                          value={formik.values.newPassword2}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.newPassword2 &&
                            Boolean(formik.errors.newPassword2)
                          }
                          helperText={
                            formik.touched.newPassword2 &&
                            formik.errors.newPassword2
                          }
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row">
                          <AnimateButton>
                            <Button variant="contained" type="submit">
                              <FormattedMessage id="general.actions.save" />
                            </Button>
                          </AnimateButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </SubCard>
                </Grid>
              </Grid>
            </Grid>
            <Grid item sm={6} md={4}>
              <Grid container spacing={gridSpacing}>
                <Grid item xs={12}>
                  <SubCard
                    title={
                      <FormattedMessage id="profile.security.delete_account" />
                    }
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body1">
                          <FormattedMessage id="profile.security.delete_account_helper" />
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row">
                          {/* eslint-disable-next-line */}
                          <a href="mailto:contact@eventpxl.com">
                            contact@eventpxl.com
                          </a>
                        </Stack>
                      </Grid>
                    </Grid>
                  </SubCard>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default Security;
